// Hücre değerinden normalize değere giden saf fonksiyonlar (spec §3, ADR-0002). G/Ç yok.
// Girdi: exceljs hücre değeri (Date | number | string | formül/zengin metin nesnesi | null).
export type Result<T> = { ok: true; value: T } | { ok: false; reason: string };
const ok = <T>(value: T): Result<T> => ({ ok: true, value });
const fail = (reason: string): Result<never> => ({ ok: false, reason });

export type NumberFormat = "tr" | "en" | "plain";
export const NUMBER_FORMATS: NumberFormat[] = ["tr", "en", "plain"];
export type DateFormat = "dd.mm.yyyy" | "yyyy-mm-dd"; // Eğik çizgili metin tarih (GG/AA ve AA/GG) hiçbir profilde kabul edilmez: kullanıcı kararı
export const DATE_FORMATS: DateFormat[] = ["dd.mm.yyyy", "yyyy-mm-dd"];

/** exceljs hücre değerini sade değere indirger; boş hücre null. ADR-0002: formülde `result`, zengin metinde metin. */
export function cellValue(v: unknown): unknown {
  if (v === null || v === undefined) return null;
  if (v instanceof Date || typeof v === "number" || typeof v === "boolean") return v;
  if (typeof v === "string") return v.trim() === "" ? null : v;
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("result" in o) return cellValue(o.result);
    if (Array.isArray(o.richText)) return cellValue(o.richText.map((r: any) => r.text ?? "").join(""));
    if (typeof o.text === "string") return cellValue(o.text);
    if (typeof o.error === "string") return o.error;
  }
  return String(v);
}

/** Karşılaştırma için sadeleştirme (spec §2): küçük harf, Türkçe katlama, boşluk ve noktalama silinir. */
export const fold = (s: string) =>
  s.replace(/İ/g, "i").toLowerCase()
    .replace(/ı/g, "i").replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ö/g, "o").replace(/ü/g, "u")
    .replace(/[^a-z0-9]/g, "");

const pad = (n: number) => String(n).padStart(2, "0");

// ---- Sayı ----
const NUM_RE: Record<NumberFormat, RegExp> = {
  tr: /^[+-]?(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/,
  en: /^[+-]?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/,
  plain: /^[+-]?\d+(\.\d+)?$/,
};

/** Excel sayı hücresi olduğu gibi; metin profil biçimine göre. */
export function parseNumber(v: unknown, format: NumberFormat): Result<number> {
  if (typeof v === "number") return Number.isFinite(v) ? ok(v) : fail("INVALID_NUMBER");
  if (typeof v !== "string") return fail("INVALID_NUMBER");
  const s = v.trim();
  if (!NUM_RE[format].test(s)) return fail("INVALID_NUMBER");
  const n = Number(format === "tr" ? s.replace(/\./g, "").replace(",", ".") : format === "en" ? s.replace(/,/g, "") : s);
  return Number.isFinite(n) ? ok(n) : fail("INVALID_NUMBER");
}

export type FormatSuggestion<F> = { status: "ok"; format: F } | { status: "ambiguous"; sample: string } | { status: "none" };

// Tek örnek: belirleyici biçim, "ambiguous" (yalnız nokta + 3 hane ya da yalnız virgül + 3 hane) ya da null (karar vermez).
function classify(x: string): NumberFormat | "ambiguous" | null {
  const s = x.trim().replace(/^[+-]/, "");
  const dots = s.split(".").length - 1;
  const commas = s.split(",").length - 1;
  if (dots && commas) return s.lastIndexOf(",") > s.lastIndexOf(".") ? (NUM_RE.tr.test(s) ? "tr" : null) : NUM_RE.en.test(s) ? "en" : null;
  if (commas) return commas > 1 ? (NUM_RE.en.test(s) ? "en" : null) : /^\d{1,3},\d{3}$/.test(s) ? "ambiguous" : NUM_RE.tr.test(s) ? "tr" : null;
  if (dots) return dots > 1 ? (NUM_RE.tr.test(s) ? "tr" : null) : /^\d{1,3}\.\d{3}$/.test(s) ? "ambiguous" : NUM_RE.plain.test(s) ? "plain" : null;
  return null; // ayraçsız: karar vermez
}

/**
 * Kolon örneklerinden sayı biçimi önerisi (spec §3). Belirleyici ilk örnek kazanır; uymayan değerler parseNumber'da satır hatası olur.
 * Yalnız `117.000` gibi belirsiz örnek varsa sistem karar vermez: `ambiguous`.
 */
export function suggestNumberFormat(samples: unknown[]): FormatSuggestion<NumberFormat> {
  let amb: string | undefined;
  for (const x of samples) {
    if (typeof x !== "string") continue;
    const c = classify(x);
    if (c === "ambiguous") amb ??= x.trim();
    else if (c) return { status: "ok", format: c };
  }
  return amb ? { status: "ambiguous", sample: amb } : { status: "ok", format: "plain" };
}

// ---- Tarih ve saat ----
const DATE_RE: Record<DateFormat, RegExp> = {
  "dd.mm.yyyy": /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
  "yyyy-mm-dd": /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
};
const isoDate = (y: number, m: number, d: number): Result<string> => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? ok(`${y}-${pad(m)}-${pad(d)}`) : fail("INVALID_DATE");
};

/** Çıktı `YYYY-AA-GG`. Excel tarih UTC bileşenleriyle okunur (ADR-0002); sayı Excel seri tarihi sayılır. Metin profil biçimine uymalı. */
export function parseDate(v: unknown, format: DateFormat | null): Result<string> {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? fail("INVALID_DATE") : ok(`${v.getUTCFullYear()}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())}`);
  if (typeof v === "number") return v >= 1 && v < 2958466 ? parseDate(new Date(Date.UTC(1899, 11, 30) + Math.floor(v) * 86400000), null) : fail("INVALID_DATE");
  if (typeof v !== "string" || !format) return fail("INVALID_DATE");
  const m = DATE_RE[format].exec(v.trim());
  if (!m) return fail("INVALID_DATE");
  return format === "yyyy-mm-dd" ? isoDate(+m[1]!, +m[2]!, +m[3]!) : isoDate(+m[3]!, +m[2]!, +m[1]!);
}

/** Tarih biçimi önerisi: nokta ya da ISO. Eğik çizgili örnek biçim vermez (satır hatası olur), soru da dönmez. */
export function suggestDateFormat(samples: unknown[]): FormatSuggestion<DateFormat> {
  for (const x of samples) {
    if (typeof x !== "string") continue;
    const s = x.trim();
    if (DATE_RE["dd.mm.yyyy"].test(s)) return { status: "ok", format: "dd.mm.yyyy" };
    if (DATE_RE["yyyy-mm-dd"].test(s)) return { status: "ok", format: "yyyy-mm-dd" };
  }
  return { status: "none" };
}

/** Çıktı `SS:DD`. Excel saat hücresi 1899-12-30 tabanlı Date (UTC), sayı günün kesri, metin `SS:DD`. */
export function parseTime(v: unknown): Result<string> {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? fail("INVALID_TIME") : ok(`${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}`);
  if (typeof v === "number") {
    const min = Math.round(v * 1440);
    return v >= 0 && min < 1440 ? ok(`${pad(Math.floor(min / 60))}:${pad(min % 60)}`) : fail("INVALID_TIME");
  }
  const m = typeof v === "string" ? /^(\d{1,2}):(\d{2})$/.exec(v.trim()) : null;
  return m && +m[1]! < 24 && +m[2]! < 60 ? ok(`${pad(+m[1]!)}:${m[2]}`) : fail("INVALID_TIME");
}

export const combineDateTime = (date: string, time: string) => `${date}T${time}`;

// ---- Birim, kategorik değer, belge no ----
export const UNITS: Record<string, { unit: string; factor: number }> = {
  kg: { unit: "KG", factor: 1 }, kgm: { unit: "KG", factor: 1 }, ton: { unit: "KG", factor: 1000 },
  adet: { unit: "ADET", factor: 1 }, mt: { unit: "MT", factor: 1 }, m2: { unit: "M2", factor: 1 },
};
/** TON → KG, çarpan 1000. Bilinmeyen birim hata. */
export function normalizeUnit(v: unknown): Result<{ unit: string; factor: number }> {
  const u = typeof v === "string" ? UNITS[fold(v)] : undefined;
  return u ? ok(u) : fail("UNKNOWN_UNIT");
}

export const ENUMS = {
  movementType: { giris: "goods_receipt", cikis: "goods_issue", sarf: "consumption", uretim: "production", transfer: "transfer", sevk: "shipment" },
  entryType: { arac: "vehicle", personel: "staff", ziyaretci: "visitor" },
  direction: { giris: "in", cikis: "out" },
  commitmentType: { ithalat: "imports", ihracat: "exports" },
} as const satisfies Record<string, Record<string, string>>;
export type EnumField = keyof typeof ENUMS;

/** Ham Türkçe değer doğrudan İngilizce enum'a. Önce profilin değer eşlemesi (`valueMap`), sonra yerleşik eş-adlar. Bilinmeyen: hata (E2b'de eşleme satırı açılır). */
// UNKNOWN_VALUE iç sinyaldir: satır hatası değil, validateRows bunu unknownValues (VALUE_MAP_MISSING) listesine çevirir.
export function normalizeEnum(field: EnumField, v: unknown, valueMap: Record<string, string> = {}): Result<string> {
  if (typeof v !== "string") return fail("UNKNOWN_VALUE");
  const key = fold(v);
  const mapped = Object.entries(valueMap).find(([k]) => k === v.trim() || fold(k) === key)?.[1];
  const target = mapped ?? (ENUMS[field] as Record<string, string>)[key];
  return target && (Object.values(ENUMS[field]) as string[]).includes(target) ? ok(target) : fail("UNKNOWN_VALUE");
}

/** Baş/son boşluk kırpılır, büyük harfe çevrilir: `im-2026/2104 ` = `IM-2026/2104`. */
export const normalizeDocNo = (v: unknown): Result<string> =>
  typeof v === "string" || typeof v === "number" ? ok(String(v).trim().toUpperCase()) : fail("INVALID_TEXT");
